import { useCallback, useEffect, useState } from 'react';

import { ChangeStep } from '../changeModalSteps';

interface UseWizardNavigationParams {
  visibleSteps: ChangeStep[];
  initialStep: ChangeStep;
  opened: boolean;
  beforeAdvance?: (from: ChangeStep) => boolean;
}

interface WizardNavigation {
  currentStep: ChangeStep;
  setCurrentStep: (step: ChangeStep) => void;
  goNext: () => void;
  goBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export default function useWizardNavigation({
  visibleSteps,
  initialStep,
  opened,
  beforeAdvance,
}: UseWizardNavigationParams): WizardNavigation {
  const [currentStep, setCurrentStep] = useState<ChangeStep>(initialStep);

  useEffect(() => {
    if (!opened) {
      setCurrentStep(initialStep);
    }
  }, [opened, initialStep]);

  useEffect(() => {
    if (!visibleSteps.includes(currentStep)) {
      setCurrentStep(visibleSteps[visibleSteps.length - 1]);
    }
  }, [visibleSteps, currentStep]);

  const currentIndex = Math.max(0, visibleSteps.indexOf(currentStep));
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === visibleSteps.length - 1;

  const goNext = useCallback(() => {
    if (beforeAdvance && !beforeAdvance(currentStep)) {
      return;
    }
    const next = visibleSteps[currentIndex + 1];
    if (next) {
      setCurrentStep(next);
    }
  }, [beforeAdvance, currentStep, currentIndex, visibleSteps]);

  const goBack = useCallback(() => {
    const prev = visibleSteps[currentIndex - 1];
    if (prev) {
      setCurrentStep(prev);
    }
  }, [currentIndex, visibleSteps]);

  return { currentStep, setCurrentStep, goNext, goBack, isFirstStep, isLastStep };
}
